"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ColumnDef } from "@tanstack/react-table";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { getInitials } from "@/lib/utils";
import { DataPendaftaran } from "@/types";
import {
  AlertCircle,
  ArrowUpDown,
  CheckCircle2,
  Clock,
  FileSearch,
} from "lucide-react";
import { Link } from "react-router-dom";

export const columns: ColumnDef<DataPendaftaran>[] = [
  // 1. CHECKBOX
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },

  // 2. PROFIL (Gabungan Foto, Nama, dan Email agar hemat tempat & elegan)
  {
    id: "profil",
    accessorFn: (row) => row.data_siswa?.nama_lengkap, // Untuk sorting berdasarkan nama
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="pl-0 hover:bg-transparent"
        >
          Profil Peserta
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const siswa = row.original?.data_siswa;
      return (
        <div className="flex items-center gap-3 py-1">
          <Avatar className="h-10 w-10 border border-slate-200 dark:border-slate-700">
            <AvatarImage
              src={siswa?.path_foto || ""}
              alt={siswa?.nama_lengkap || "Foto"}
              className="object-cover"
            />
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
              {getInitials(siswa?.nama_lengkap || "P")}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col max-w-[200px] lg:max-w-[250px]">
            <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
              {siswa?.nama_lengkap || "-"}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {siswa?.email || "-"}
            </span>
          </div>
        </div>
      );
    },
  },

  // 3. NOMOR PENDAFTARAN
  {
    accessorKey: "nomor_pendaftaran",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="pl-0 hover:bg-transparent"
      >
        No. Daftar
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    cell: ({ row }) => (
      <Badge
        variant="outline"
        className="font-mono text-xs bg-slate-50 dark:bg-slate-800"
      >
        {row.original?.nomor_pendaftaran ?? "-"}
      </Badge>
    ),
  },

  // 4. PROGRAM STUDI
  {
    id: "prodi",
    accessorFn: (row) => row.unitkerja?.nama,
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="pl-0 hover:bg-transparent"
      >
        Program Studi
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
    cell: ({ row }) => {
      const prodi = row.original?.unitkerja?.nama;
      return (
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {prodi || "-"}
        </span>
      );
    },
  },

  // 5. NO HANDPHONE (Opsional, bisa di-hide by default di pengaturan tabel)
  {
    accessorKey: "data_siswa.handphone",
    header: "Kontak",
    cell: ({ row }) => (
      <span className="text-sm text-slate-600 dark:text-slate-400">
        {row.original?.data_siswa?.handphone || "-"}
      </span>
    ),
  },

  // 6. STATUS VALIDASI
  {
    id: "status_validasi",
    header: "Status Validasi",
    cell: ({ row }) => {
      const dataPendaftaran = row.original;

      const isValidatedJumlahTanggungan =
        dataPendaftaran?.is_valid_tanggungan !== null;
      const isValidatedDataPantiAsuhan = !row.original?.data_siswa
        ?.path_bukti_panti_asuhan
        ? true
        : row.original?.data_siswa?.status_valid_panti_asuhan !== null; // Perbaikan logika: asal tidak null berarti sudah diperiksa

      const isValidatedDataKeluarga =
        (isValidatedDataPantiAsuhan &&
          dataPendaftaran?.data_keluarga.every(
            (item) => item.is_valid !== null,
          )) ||
        false;

      const isValidatedDataAset =
        dataPendaftaran?.data_aset.every((item) => item.is_valid !== null) ||
        false;

      const isValidatedDataBerkas =
        dataPendaftaran?.data_berkas.every((item) => item.is_valid !== null) ||
        false;

      const isAllValidated =
        isValidatedDataKeluarga &&
        isValidatedJumlahTanggungan &&
        isValidatedDataAset &&
        isValidatedDataBerkas;
      const isNoneValidated =
        !isValidatedDataKeluarga &&
        !isValidatedJumlahTanggungan &&
        !isValidatedDataAset &&
        !isValidatedDataBerkas;

      if (isAllValidated) {
        return (
          <Badge
            variant="success"
            className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-none"
          >
            <CheckCircle2 className="w-3 h-3 mr-1" /> Selesai
          </Badge>
        );
      }

      if (isNoneValidated) {
        return (
          <Badge
            variant="secondary"
            className="bg-slate-100 text-slate-600 hover:bg-slate-200 border-none dark:bg-slate-800 dark:text-slate-400"
          >
            <Clock className="w-3 h-3 mr-1" /> Belum Diperiksa
          </Badge>
        );
      }

      // Kondisi Parsial (Sedang di proses tapi belum selesai semua)
      const unvalidatedSections = [];
      if (!isValidatedDataKeluarga) unvalidatedSections.push("Keluarga");
      if (!isValidatedJumlahTanggungan) unvalidatedSections.push("Tanggungan");
      if (!isValidatedDataAset) unvalidatedSections.push("Aset");
      if (!isValidatedDataBerkas) unvalidatedSections.push("Berkas");

      return (
        <div className="flex flex-col gap-1 items-start">
          <Badge
            // variant="warning"
            className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-none"
          >
            <AlertCircle className="w-3 h-3 mr-1" /> Proses
          </Badge>
          <span className="text-[10px] text-slate-500 leading-tight">
            Kurang: {unvalidatedSections.slice(0, 2).join(", ")}{" "}
            {unvalidatedSections.length > 2 && "..."}
          </span>
        </div>
      );
    },
  },

  // 7. AKSI (Paling Kanan)
  {
    id: "aksi",
    header: () => <div className="text-right">Aksi</div>,
    cell: ({ row }) => {
      // 1. Inisialisasi params untuk link tujuan
      const params = new URLSearchParams();
      params.append(
        "nomor_pendaftaran",
        String(row.original.nomor_pendaftaran),
      );

      // 2. Ambil URL saat ini (path + pencarian/filter)
      const currentPath = window.location.pathname;
      const currentSearch = window.location.search;

      // 3. Tambahkan ke params. URLSearchParams akan otomatis meng-encode string ini.
      params.append("returnTo", `${currentPath}${currentSearch}`);

      return (
        <div className="flex justify-end">
          <Link
            target="_blank"
            // 4. Outputkan semua params menggunakan .toString()
            to={`/verifikator/verifikasi-berkas/view?${params.toString()}`}
          >
            <Button
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white shadow-sm"
            >
              <FileSearch className="w-4 h-4 mr-2" />
              Periksa
            </Button>
          </Link>
        </div>
      );
    },
  },
  {
    id: "verifikator",
    // Pastikan accessorFn berada langsung di bawah id
    accessorFn: (row) => {
      // Kita ambil data user_verifikator1
      const user =
        row.verifikator_ukt_kelompok_detail?.verkelompokukt?.user_verifikator1;

      // Type checking: Pastikan user bukan string dan memiliki properti nama_lengkap
      if (user && typeof user === "object" && "nama_lengkap" in user) {
        return user.nama_lengkap;
      }

      return "-";
    },
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="pl-0 hover:bg-transparent"
      >
        Verifikator
        <ArrowUpDown className="w-4 h-4 ml-2" />
      </Button>
    ),
  },
];
